import React, { useRef, useEffect, useState } from 'react';
import { Pause, ArrowUp } from 'lucide-react';
import debounce from 'lodash/debounce';
import { validateChatMessage } from '../services/validationService';
import { useTranslate } from '../lib/i18n';
import analyticsService from '../services/analyticsService';
import VoiceRecorder, {
  type RecordingState,
  type VoiceRecordingError,
  MAX_RECORDING_DURATION_SECONDS,
} from './VoiceRecorder';
import ErrorToast from './ErrorToast';
import WaveformVisualizer from './WaveformVisualizer';

const MAX_ROWS = 3;
const LINE_HEIGHT = 24;
const BASE_PADDING = 8;

const ENABLE_VOICE_INPUT = true;

interface ChatInputProps {
  message: string;
  setMessage: (message: string) => void;
  onSendMessage: () => void;
  onCancelMessage: () => void;
  hasSuicideRisk: boolean;
  isSendingMessage: boolean;
  isCreatingConversation: boolean;
  validationError: string | null;
  setValidationError: (error: string | null) => void;
  voiceRecorderState: RecordingState;
  setVoiceRecorderState: (state: RecordingState) => void;
  conversationId: string | null;
}

const ChatInput = ({
  message,
  setMessage,
  onSendMessage,
  onCancelMessage,
  hasSuicideRisk,
  isSendingMessage,
  isCreatingConversation,
  validationError,
  setValidationError,
  voiceRecorderState,
  setVoiceRecorderState,
  conversationId,
}: ChatInputProps) => {
  const t = useTranslate();
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [voiceError, setVoiceError] = useState<string | null>(null);
  const [recordingStream, setRecordingStream] = useState<MediaStream | null>(null);
  const [remainingSeconds, setRemainingSeconds] = useState<number>(MAX_RECORDING_DURATION_SECONDS);
  const [transcribedText, setTranscribedText] = useState<string | null>(null);
  const [hasEditedTranscription, setHasEditedTranscription] = useState<boolean>(false);

  // Helper function to handle validation errors
  const handleValidationError = (validation: ReturnType<typeof validateChatMessage>) => {
    if (!validation.success && validation.error) {
      if (validation.error.includes('||')) {
        const [key, char] = validation.error.split('||');
        setValidationError(t(key || '', { char }));
      } else {
        setValidationError(t(validation.error));
      }
    } else {
      setValidationError(null);
    }
  };

  // Function to adjust textarea height
  const adjustTextareaHeight = debounce(() => {
    if (!textareaRef.current) return;

    textareaRef.current.style.height = 'auto';
    const scrollHeight = textareaRef.current.scrollHeight;
    const maxHeight = LINE_HEIGHT * MAX_ROWS + BASE_PADDING * 2;
    textareaRef.current.style.height = Math.min(scrollHeight, maxHeight) + 'px';
  }, 150);

  useEffect(() => {
    adjustTextareaHeight();
  }, [message]);

  const handleMessageChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newMessage = e.target.value;
    setMessage(newMessage);

    // Check if user edited transcribed text
    if (transcribedText !== null && newMessage !== transcribedText) {
      if (!hasEditedTranscription) {
        setHasEditedTranscription(true);
      }
    }

    // Validate message (including transcribed text)
    const validation = validateChatMessage(newMessage, false);
    handleValidationError(validation);
  };

  // Validate transcribed text when message changes from voice input
  useEffect(() => {
    if (message && voiceRecorderState === 'idle') {
      // Revalidate when message was likely set from transcription
      const validation = validateChatMessage(message, false);
      handleValidationError(validation);
    }
  }, [message, voiceRecorderState]);

  // Input container can be disabled while creating conversation
  const isInputDisabled = isCreatingConversation || hasSuicideRisk || isSendingMessage;

  // Determine if user has input text (for dynamic button)
  const hasUserInput = message.trim().length > 0;

  // Check if voice recording is active (affects UI state)
  const isRecording = voiceRecorderState === 'recording';
  const isProcessingAudio = voiceRecorderState === 'processing';
  const hasVoiceError = voiceRecorderState === 'error';

  // Handle remaining seconds update from VoiceRecorder
  const handleRemainingSecondsChange = (seconds: number) => {
    setRemainingSeconds(seconds);
  };

  // Get dynamic placeholder text based on current state
  const getPlaceholderText = () => {
    if (hasSuicideRisk) return t('aiChatbot.messagingDisabled');
    if (isCreatingConversation) return t('common.labels.loading');
    if (isSendingMessage) return t('aiChatbot.waitingForResponse');
    if (isRecording) return t('aiChatbot.recordingPrompt');
    if (isProcessingAudio) return t('voiceRecorder.processing');
    if (hasVoiceError) return t('voiceRecorder.typeInsteadPrompt');
    return t('aiChatbot.writeMessagePrompt');
  };

  // Handle send button click
  const handleSend = () => {
    if (hasSuicideRisk || isSendingMessage || validationError) return;

    // Track if transcribed text was sent as-is or edited
    if (transcribedText !== null && message.trim()) {
      if (!hasEditedTranscription && message === transcribedText) {
        analyticsService.trackEvent({
          eventName: 'speechToTextTranscriptionSentAsIs',
          properties: {
            characterCount: message.length,
          },
        });
      } else if (hasEditedTranscription) {
        analyticsService.trackEvent({
          eventName: 'speechToTextTranscriptionSentEdited',
          properties: {
            originalLength: transcribedText.length,
            editedLength: message.length,
          },
        });
      }
    }

    onSendMessage();
    setMessage('');
    setTranscribedText(null);
    setHasEditedTranscription(false);
  };

  // Handle keyboard events in textarea
  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (
      e.key === 'Enter' &&
      !e.shiftKey &&
      !hasSuicideRisk &&
      !isSendingMessage &&
      !validationError
    ) {
      e.preventDefault();
      if (hasUserInput) {
        handleSend();
      }
    }
  };

  // Handle voice transcription completion
  const handleTranscriptionComplete = (text: string) => {
    setMessage(text);
    setTranscribedText(text);
    setHasEditedTranscription(false);

    // Validate the transcribed text
    const validation = validateChatMessage(text, false);
    handleValidationError(validation);
  };

  // Handle voice recording error by focusing text input
  const handleVoiceError = (error: VoiceRecordingError) => {
    // Only set error if we don't already have one showing
    if (!voiceError) {
      // Show toast notification based on error type
      const errorMessage =
        error.type === 'permission'
          ? t('voiceRecorder.permissionDenied')
          : t('voiceRecorder.errorOccurredTypeInstead');
      setVoiceError(errorMessage);

      // Focus the text input when voice recording fails
      if (textareaRef.current) {
        textareaRef.current.focus();
      }
    }
  };

  return (
    <>
      {/* Voice error toast */}
      <ErrorToast
        error={voiceError}
        duration={3000}
        onClose={() => {
          setVoiceError(null);
        }}
      />

      <div className="shrink-0 p-4 shadow-2xl">
        <div className="relative mx-auto max-w-2xl">
          <div className="relative flex items-center gap-2 rounded-[10px] border bg-input/25 p-2 overflow-hidden focus-within:border-ring">
            <div className="relative z-[0] flex-grow">
              {isRecording && recordingStream && (
                <div
                  className="absolute inset-0 z-[1] flex items-center px-3 pointer-events-none"
                  aria-label={t('aiChatbot.recordingPrompt')}
                >
                  <WaveformVisualizer
                    stream={recordingStream}
                    className="w-full"
                    remainingSeconds={remainingSeconds}
                    maxDuration={MAX_RECORDING_DURATION_SECONDS}
                  />
                </div>
              )}
              <textarea
                ref={textareaRef}
                value={message}
                onChange={handleMessageChange}
                onKeyDown={handleKeyDown}
                className={`prevent-zoom w-full resize-none overflow-y-auto px-3 pt-3 pb-2 text-sm text-foreground leading-4 transition-colors duration-500 ease-out focus:outline-none ${
                  validationError ? 'border-destructive' : 'border-secondary'
                } ${validationError ? 'focus:border-destructive' : 'focus:border-secondary'} ${isRecording ? 'opacity-0' : ''}`}
                disabled={isInputDisabled}
                rows={1}
                aria-label={t('aiChatbot.textareaLabel')}
              />
              {!message && (
                <span
                  className={`pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm transition-colors duration-500 ease-out text-muted-foreground ${isRecording ? 'opacity-0' : ''}`}
                >
                  {getPlaceholderText()}
                </span>
              )}
            </div>

            {/* Dynamic action button */}
            {isSendingMessage ? (
              <button
                onClick={onCancelMessage}
                className="relative z-[2] flex items-center justify-center rounded-[10px] bg-muted px-4 py-2 text-muted-foreground transition-all duration-500 ease-out hover:bg-muted/50"
                aria-label={t('aiChatbot.cancel')}
              >
                <Pause className="h-5 w-5 transition-transform duration-200" />
              </button>
            ) : ENABLE_VOICE_INPUT && !hasUserInput ? (
              // Microphone / recording button
              <div className="relative z-[2]">
                <VoiceRecorder
                  onTranscriptionComplete={handleTranscriptionComplete}
                  onRecordingStateChange={setVoiceRecorderState}
                  onError={handleVoiceError}
                  onClearError={() => setVoiceError(null)}
                  onMediaStream={setRecordingStream}
                  onRemainingSecondsChange={handleRemainingSecondsChange}
                  disabled={isInputDisabled || isProcessingAudio}
                  maxDurationSeconds={MAX_RECORDING_DURATION_SECONDS}
                  className="items-end"
                  conversationId={conversationId}
                />
              </div>
            ) : (
              // Send button
              <button
                onClick={handleSend}
                className={`relative z-[2] flex items-center justify-center rounded-[10px] bg-secondary px-4 py-2 text-secondary-foreground transition-all duration-500 ease-out hover:bg-secondary/90 focus:bg-secondary/90 ${
                  isInputDisabled || !!validationError || !hasUserInput
                    ? 'cursor-not-allowed opacity-50'
                    : ''
                }`}
                disabled={isInputDisabled || !!validationError || !hasUserInput}
                aria-label={t('common.buttons.send')}
              >
                <ArrowUp className="h-5 w-5 transition-transform duration-200" />
              </button>
            )}
          </div>

          {/* Error messages */}
          {validationError && (
            <p className="absolute -bottom-5 my-1 text-xs text-destructive">{validationError}</p>
          )}

          <p className="mt-2 text-center text-[10px] leading-3 text-muted-foreground">
            {t('aiChatbot.botDisclaimer')}
          </p>
        </div>
      </div>
    </>
  );
};

export default ChatInput;
